/**
 * User List Page
 *
 * Main admin page for managing users.
 * Features: advanced data table with search, filtering, server-side pagination, create user, auto-refresh.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconUsers,
  IconUserPlus,
  IconAlertCircle,
  IconUserCheck,
  IconUserOff,
  IconLock,
  IconEye,
  IconFilter,
  IconRefresh,
} from '@tabler/icons-react';
import {
  Container,
  Stack,
  Card,
  Group,
  Button,
  Alert,
  Text,
  Loader,
  Grid,
  Avatar,
  Badge,
  ActionIcon,
  Drawer,
} from '../../../components/ui';
import { FadeIn, SlideIn } from '../../../components/animations';
import { DataTable, type DataTableColumn } from '../../../components/advanced';
import { UserFilters } from './components/UserFilters';
import { NewUser } from './NewUser';
import { useUserFilters } from './hooks/useUserFilters';
import { useHttpClient, useRoleService } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../config/ccms';
import type { UserModelView, RoleModelView, ResponseData } from './types';

// Adapter type for DataTable compatibility (requires non-nullable id)
type UserRow = UserModelView & { id: string };

export default function UserListPage() {
  const navigate = useNavigate();
  const httpClient = useHttpClient();
  const roleService = useRoleService();

  // State management
  const [allUsers, setAllUsers] = useState<UserRow[]>([]); // All loaded users
  const [roles, setRoles] = useState<RoleModelView[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [newUserModalOpened, setNewUserModalOpened] = useState(false);
  const [filtersDrawerOpened, setFiltersDrawerOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Hooks
  const {
    filters,
    updateFilter,
    updateFilters,
    clearFilters,
  } = useUserFilters();

  /**
   * Apply client-side filters to all users
   */
  const filteredUsers = useMemo(() => {
    let filtered = [...allUsers];

    // Search filter
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        // Search across multiple fields
        const fullName = `${user.firstName || ''} ${user.surname || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.cellphone || '').toLowerCase();
        const displayName = (user.fullName || '').toLowerCase();

        return (
          fullName.includes(query) ||
          email.includes(query) ||
          phone.includes(query) ||
          displayName.includes(query)
        );
      });
    }

    // Role filter
    if (filters.selectedRole) {
      filtered = filtered.filter((u) => u.roles?.includes(filters.selectedRole!));
    }

    // Status filter (Active/Inactive)
    if (filters.statusFilter !== 'all') {
      filtered = filtered.filter((u) => {
        const isActive = !u.isLockedOut;
        return filters.statusFilter === 'active' ? isActive : !isActive;
      });
    }

    // Email confirmed filter
    if (filters.emailConfirmedFilter !== 'all') {
      filtered = filtered.filter((u) => {
        return filters.emailConfirmedFilter === 'confirmed' ? u.emailConfirmed : !u.emailConfirmed;
      });
    }

    // Locked out filter
    if (filters.lockedOutFilter !== 'all') {
      filtered = filtered.filter((u) => {
        return filters.lockedOutFilter === 'locked' ? u.isLockedOut : !u.isLockedOut;
      });
    }

    // Date range filters
    if (filters.createdFrom) {
      const fromDate = new Date(filters.createdFrom);
      filtered = filtered.filter((u) => {
        if (!u.createdDate) return false;
        return new Date(u.createdDate) >= fromDate;
      });
    }

    if (filters.createdTo) {
      const toDate = new Date(filters.createdTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire day
      filtered = filtered.filter((u) => {
        if (!u.createdDate) return false;
        return new Date(u.createdDate) <= toDate;
      });
    }

    return filtered;
  }, [allUsers, filters, searchQuery]);

  /**
   * Calculate stats from all loaded users (not filtered)
   */
  const stats = useMemo(() => {
    const active = allUsers.filter((u) => !u.isLockedOut).length;
    const inactive = allUsers.filter((u) => u.isLockedOut && !u.lockoutEnabled).length;
    const locked = allUsers.filter((u) => u.isLockedOut && u.lockoutEnabled).length;

    return {
      total: allUsers.length,
      active,
      inactive,
      locked,
    };
  }, [allUsers]);

  /**
   * Fetch all users progressively (stream-like loading)
   * No filters applied - we load ALL users and filter client-side
   */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadingProgress(0);

    try {
      const allLoadedUsers: UserRow[] = [];
      let page = 1;
      const pageSize = 50; // Fetch 50 at a time
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('pageSize', String(pageSize));

        const url = `${getApiUrl('users', '/api/admin/users')}?${params.toString()}`;
        const response = await httpClient.get<ResponseData<UserModelView[]>>(url);

        const responseData = (response as any).data?.data || response.data || [];
        const userData: UserModelView[] = Array.isArray(responseData) ? responseData : [];

        const validUsers: UserRow[] = userData
          .filter((u): u is UserModelView & { id: string } => !!u.id)
          .map((u) => ({ ...u, id: u.id }));

        allLoadedUsers.push(...validUsers);

        // Update progress
        setLoadingProgress(Math.min(95, page * 10));

        // Update UI with current data
        setAllUsers([...allLoadedUsers]);

        // Check if we got less than pageSize (last page)
        if (validUsers.length < pageSize) {
          hasMore = false;
        } else {
          page++;
        }
      }

      setLoadingProgress(100);
      setAllUsers(allLoadedUsers);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch users';
      setError(errorMessage);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  }, [httpClient]);

  /**
   * Fetch available roles for filtering
   */
  const fetchRoles = useCallback(async () => {
    try {
      const rolesData = await roleService.getAll();
      setRoles(rolesData);
    } catch (err: any) {
      console.error('Error fetching roles:', err);
    }
  }, [roleService]);

  /**
   * Initial load: Fetch users and roles
   */
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  /**
   * Handle view user
   */
  const handleViewUser = useCallback(
    (userId: string) => {
      navigate(`/admin/users/${userId}`);
    },
    [navigate]
  );

  /**
   * Handle new user success
   */
  const handleNewUserSuccess = useCallback(() => {
    setNewUserModalOpened(false);
    fetchUsers(); // Refresh list
  }, [fetchUsers]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (page: number) => {
      updateFilter('page', page);
    },
    [updateFilter]
  );

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      updateFilter('pageSize', pageSize);
      updateFilter('page', 1); // Reset to first page
    },
    [updateFilter]
  );

  /**
   * Format date helper
   */
  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Get user initials
   */
  const getInitials = (user: UserRow) => {
    const first = user.firstName?.charAt(0) || '';
    const last = user.surname?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  /**
   * Define DataTable columns - Simplified to 6 columns
   */
  const columns: DataTableColumn<UserRow>[] = useMemo(
    () => [
      {
        key: 'user',
        label: 'User',
        sortable: true,
        render: (_, user) => (
          <Group gap="sm">
            <Avatar src={user.thumbnail || user.avatar} size="sm" radius="md">
              {getInitials(user)}
            </Avatar>
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                {user.fullName || `${user.firstName} ${user.surname}`}
              </Text>
              <Text size="xs" c="dimmed">
                {user.email}
              </Text>
            </Stack>
          </Group>
        ),
      },
      {
        key: 'cellphone',
        label: 'Contact',
        render: (value) => <Text size="sm">{value || '-'}</Text>,
      },
      {
        key: 'roles',
        label: 'Roles',
        render: (value: string[]) => {
          if (!value || value.length === 0) {
            return (
              <Text size="xs" c="dimmed">
                No roles
              </Text>
            );
          }
          const displayRoles = value.slice(0, 2);
          const remaining = value.length - 2;
          return (
            <Group gap={4}>
              {displayRoles.map((role, idx) => (
                <Badge key={idx} size="sm" variant="light" color="blue">
                  {role}
                </Badge>
              ))}
              {remaining > 0 && (
                <Badge size="sm" variant="light" color="gray">
                  +{remaining}
                </Badge>
              )}
            </Group>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        render: (_, user) => (
          <Group gap={4}>
            <Badge size="sm" color={!user.isLockedOut ? 'green' : 'gray'} variant="light">
              {!user.isLockedOut ? 'Active' : 'Inactive'}
            </Badge>
            <Badge size="sm" color={user.emailConfirmed ? 'blue' : 'orange'} variant="light">
              {user.emailConfirmed ? 'Verified' : 'Unverified'}
            </Badge>
            {user.isLockedOut && (
              <Badge size="sm" color="red" variant="filled">
                Locked
              </Badge>
            )}
          </Group>
        ),
      },
      {
        key: 'createdDate',
        label: 'Created',
        sortable: true,
        render: (value) => (
          <Text size="sm" c="dimmed">
            {formatDate(value)}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, user) => (
          <ActionIcon
            variant="light"
            size="sm"
            color="blue"
            onClick={() => handleViewUser(user.id!)}
            aria-label="View user details"
          >
            <IconEye size={14} />
          </ActionIcon>
        ),
      },
    ],
    [handleViewUser]
  );

  return (
    <Container size="xl" py="xl">
      <FadeIn delay={100}>
        <Stack gap="xl">
          {/* Header Section */}
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="sm">
                <IconUsers size={32} />
                <Text size="xl" fw={700}>
                  User Management
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {stats.total} {stats.total === 1 ? 'user' : 'users'} total
              </Text>
            </Stack>

            <Group>
              <Button
                variant="light"
                leftSection={<IconRefresh size={18} />}
                onClick={fetchUsers}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                variant="light"
                leftSection={<IconFilter size={18} />}
                onClick={() => setFiltersDrawerOpened(true)}
              >
                Filters
              </Button>
              <Button
                leftSection={<IconUserPlus size={18} />}
                onClick={() => setNewUserModalOpened(true)}
                gradientVariant="primary"
              >
                Add New User
              </Button>
            </Group>
          </Group>

          {/* Summary Stats Cards */}
          <SlideIn direction="up" delay={100}>
            <Grid>
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Card>
                  <Stack gap={4}>
                    <Group gap="xs">
                      <IconUsers size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="xs" c="dimmed" tt="uppercase">
                        Total Users
                      </Text>
                    </Group>
                    <Text size="xl" fw={700} c="blue">
                      {stats.total}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Card>
                  <Stack gap={4}>
                    <Group gap="xs">
                      <IconUserCheck size={16} color="var(--mantine-color-green-6)" />
                      <Text size="xs" c="dimmed" tt="uppercase">
                        Active
                      </Text>
                    </Group>
                    <Text size="xl" fw={700} c="green">
                      {stats.active}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Card>
                  <Stack gap={4}>
                    <Group gap="xs">
                      <IconUserOff size={16} color="var(--mantine-color-gray-6)" />
                      <Text size="xs" c="dimmed" tt="uppercase">
                        Inactive
                      </Text>
                    </Group>
                    <Text size="xl" fw={700} c="gray">
                      {stats.inactive}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Card>
                  <Stack gap={4}>
                    <Group gap="xs">
                      <IconLock size={16} color="var(--mantine-color-red-6)" />
                      <Text size="xs" c="dimmed" tt="uppercase">
                        Locked Out
                      </Text>
                    </Group>
                    <Text size="xl" fw={700} c="red">
                      {stats.locked}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </SlideIn>

          {/* Error Alert */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* DataTable */}
          <SlideIn direction="up" delay={200}>
            <Card p="lg">
              {loading ? (
                <Stack align="center" py="xl">
                  <Loader />
                  <Text size="sm" c="dimmed">
                    Loading users...
                  </Text>
                </Stack>
              ) : (
                <DataTable<UserRow>
                  data={filteredUsers}
                  columns={columns}
                  searchPlaceholder="Search users by name, email, or phone..."
                  searchValue={searchQuery}
                  onSearch={(query) => {
                    setSearchQuery(query);
                    updateFilter('page', 1); // Reset to first page on search
                  }}
                  pagination={{
                    page: filters.page,
                    pageSize: filters.pageSize,
                    total: filteredUsers.length,
                    onPageChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                  }}
                  gradientVariant="primary"
                  highlightOnHover
                  striped
                  stickyHeader
                  withRowAnimations
                  emptyMessage="No users found. Try adjusting your filters or create a new user."
                  enableInternalSort
                />
              )}
            </Card>
          </SlideIn>
        </Stack>
      </FadeIn>

      {/* New User Modal */}
      {newUserModalOpened && (
        <NewUser
          opened={newUserModalOpened}
          onClose={() => setNewUserModalOpened(false)}
          onSuccess={handleNewUserSuccess}
          availableRoles={roles}
        />
      )}

      {/* Filters Drawer */}
      <Drawer
        opened={filtersDrawerOpened}
        onClose={() => setFiltersDrawerOpened(false)}
        title="Filter Users"
        position="right"
        size="md"
      >
        <UserFilters
          filters={filters}
          roles={roles}
          onFilterChange={(newFilters) => updateFilters(newFilters)}
          onClearFilters={clearFilters}
          loading={loading}
        />
      </Drawer>
    </Container>
  );
}
